import React, { createContext, useContext } from 'react';
import { daysBetween } from '../../../../../function-utils/building-time';
import { GuideOpenBody } from '../../../../../interfaces/stay-guide';
import { useDocumentTitle } from '../../../../hooks/use-document-title';
import { formatStayDate, nightsGuestsLine, useNoReferrer } from '../../../../onboarding/stay/stay-steps';
import { StayKey, t } from '../../../../onboarding/stay/stay-strings';
import { Header, Line, StrongLine } from '../guest-checkin.styled';
import { GuideSectionId, sectionsShown } from './guide-state';
import { GuideHelpStrip } from './guide-help-strip';
import { BuildingClockNote } from './guide-format';
import {
  Frame,
  FrameEnd,
  HeroTitle,
  Title,
  NavChipButton,
  NavChipLink,
  NavList,
  QuietAnchor,
  QuietButton,
  QuietLink,
  SectionCardButton,
  SectionCardLink,
} from './guide.styled';

const L = 'en' as const;

/**
 * THE GUIDE'S FRAME (plan S7.7): S6's guest header (the listing, the stay's
 * dates, the building clock note), the section nav, the page, the help strip
 * and "Back to top".
 *
 * TWO WAYS TO MOVE. On the guest's page every section is a route
 * (/s/:token/guide/<section>); in the host preview (S7.9) the same components
 * switch sections in the page's state — no route, no guest link. The
 * GuideNavContext says which, and GuideLink renders a router Link or a button
 * accordingly, so no section component knows where it is mounted.
 */

export type GuideTarget = GuideSectionId | 'hub';

export interface GuideNav {
  /** The host preview: sections switch in-page, Share is off, no guest link. */
  preview: boolean;
  /** The guest's token (route mode); '' in the preview. */
  token: string;
  current: GuideTarget;
  go: (target: GuideTarget) => void;
}

export const GuideNavContext = createContext<GuideNav>({
  preview: false,
  token: '',
  current: 'hub',
  go: () => undefined,
});

export const useGuideNav = (): GuideNav => useContext(GuideNavContext);

export const guideHref = (token: string, target: GuideTarget): string =>
  target === 'hub' ? `/s/${token}/guide` : `/s/${token}/guide/${target}`;

export const NAV_LABEL: Record<GuideSectionId, StayKey> = {
  arrival: 'guide.navArrival',
  essentials: 'guide.navEssentials',
  local: 'guide.navLocal',
  checkout: 'guide.navCheckout',
  photos: 'guide.navPhotos',
};

type LinkKind = 'chip' | 'card' | 'quiet';

const KINDS = {
  chip: { Link: NavChipLink, Button: NavChipButton },
  card: { Link: SectionCardLink, Button: SectionCardButton },
  quiet: { Link: QuietLink, Button: QuietButton },
};

/** A move to a section: a route Link for the guest, a button in the preview. */
export const GuideLink: React.FC<{
  target: GuideTarget;
  kind: LinkKind;
  children: React.ReactNode;
  current?: boolean;
  describedBy?: string;
}> = ({ target, kind, children, current = false, describedBy }) => {
  const nav = useGuideNav();
  const { Link: AsLink, Button: AsButton } = KINDS[kind];
  if (nav.preview) {
    return (
      <AsButton
        type="button"
        onClick={() => nav.go(target)}
        aria-current={current ? 'page' : undefined}
        aria-describedby={describedBy}
      >
        {children}
      </AsButton>
    );
  }

  return (
    <AsLink
      to={guideHref(nav.token, target)}
      aria-current={current ? 'page' : undefined}
      aria-describedby={describedBy}
    >
      {children}
    </AsLink>
  );
};

/** The guest page's document-level effects (never mounted in the preview). */
export const GuideGuestEffects: React.FC = () => {
  useNoReferrer();
  useDocumentTitle(t(L, 'guide.documentTitle'));

  return null;
};

export const TOP_ID = 'guide-top';
export const TITLE_ID = 'guide-title';

/**
 * A guide page's title: the route's one h1 on the guest's page; an h2 in the
 * host preview, which sits inside a dashboard page that has its own h1.
 * Focusable (tabIndex -1) so a section change can move focus to it.
 */
export const GuideH1: React.FC<{ children: React.ReactNode; hero?: boolean }> = ({ children, hero = false }) => {
  const nav = useGuideNav();
  const As = hero ? HeroTitle : Title;

  return (
    <As as={nav.preview ? 'h2' : 'h1'} id={TITLE_ID} tabIndex={-1}>
      {children}
    </As>
  );
};

const backToTop = (e: React.MouseEvent) => {
  e.preventDefault();
  const top = document.getElementById(TOP_ID);
  if (top && typeof top.scrollIntoView === 'function') top.scrollIntoView({ block: 'start' });
  const title = document.getElementById(TITLE_ID);
  if (title) title.focus();
};

export const GuideFrame: React.FC<{ body: GuideOpenBody; children: React.ReactNode }> = ({ body, children }) => {
  const nav = useGuideNav();
  const nights = daysBetween(body.stay.check_in, body.stay.check_out);

  return (
    <Frame id={TOP_ID}>
      <Header>
        {body.stay.listing_name ? <StrongLine>{body.stay.listing_name}</StrongLine> : null}
        <Line data-at={body.stay.check_in}>
          {t(L, 'datesLine', {
            from: formatStayDate(body.stay.check_in),
            to: formatStayDate(body.stay.check_out),
            nights: Number.isFinite(nights) ? nightsGuestsLine(nights) : '',
          })}
        </Line>
        {/* One note for the page: every time below is the building's. */}
        <BuildingClockNote zone={body.zone} />
      </Header>

      <nav aria-label={t(L, 'guide.navLabel')}>
        <NavList>
          {sectionsShown(body).map(id => (
            <li key={id}>
              <GuideLink target={id} kind="chip" current={nav.current === id}>
                {t(L, NAV_LABEL[id])}
              </GuideLink>
            </li>
          ))}
        </NavList>
      </nav>

      {nav.current !== 'hub' ? (
        <GuideLink target="hub" kind="quiet">
          {t(L, 'guide.backToGuide')}
        </GuideLink>
      ) : null}

      {children}

      <GuideHelpStrip guide={body.guide} />

      <FrameEnd>
        <QuietAnchor href={`#${TOP_ID}`} onClick={backToTop}>
          {t(L, 'guide.backToTop')}
        </QuietAnchor>
      </FrameEnd>
    </Frame>
  );
};
